/**
 * Sentry Configuration for SolelyTax AI
 * Error monitoring and performance tracking
 */
import * as Sentry from '@sentry/react';

const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;

export const initSentry = () => {
  if (!SENTRY_DSN) {
    // No DSN configured — error tracking disabled. Silent fail per Commandment IX.
    return;
  }

  Sentry.init({
    dsn: SENTRY_DSN,
    
    // Environment configuration
    environment: process.env.NODE_ENV || 'development',
    
    // Release version - update this with each deployment
    release: 'solelytax@1.0.0',
    
    // Performance Monitoring - capture all in dev, 20% in production
    tracesSampleRate: process.env.NODE_ENV === 'production' ? 0.2 : 1.0,
    
    // Session Replay for debugging
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
    
    // Integrations
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],

    // Filter out noisy errors
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      'canvas.contentDocument',
      'atomicFindClose',
      // Network errors that users cause
      'Network request failed',
      'Failed to fetch',
      'NetworkError',
      'Load failed',
      // Common third-party errors
      'ResizeObserver loop',
      'Non-Error promise rejection',
    ],

    // Don't send errors from these URLs
    denyUrls: [
      /extensions\//i,
      /^chrome:\/\//i,
      /^chrome-extension:\/\//i,
      /^moz-extension:\/\//i,
    ],

    // Before sending error, you can modify or filter it
    beforeSend(event, hint) {
      // Don't send errors in development (optional - remove if you want dev errors too)
      // if (process.env.NODE_ENV === 'development') {
      //   return null;
      // }
      
      return event;
    },
  });
};

// Helper to set user context when they log in
export const setSentryUser = (user) => {
  if (user) {
    Sentry.setUser({
      id: user.user_id,
      email: user.email,
      username: user.name,
    });
  } else {
    Sentry.setUser(null);
  }
};

// Helper to add breadcrumbs for debugging
export const addBreadcrumb = (category, message, data = {}) => {
  Sentry.addBreadcrumb({
    category,
    message,
    data,
    level: 'info',
  });
};

// Helper to capture custom errors
export const captureError = (error, context = {}) => {
  Sentry.captureException(error, {
    extra: context,
  });
};

// Helper to capture messages (non-error events)
export const captureMessage = (message, level = 'info') => {
  Sentry.captureMessage(message, level);
};

// Send a test event to verify Sentry connection
export const sendTestEvent = () => {
  Sentry.captureMessage('SolelyTax Sentry Integration Test - Connection Verified!', 'info');
  return true;
};

// Expose Sentry globally for debugging (only in development)
if (typeof window !== 'undefined') {
  window.Sentry = Sentry;
  window.sendSentryTest = sendTestEvent;
}

export default Sentry;
